import React, { useState, useEffect, useRef } from 'react';
import styles from './BlogManagement.module.css';
import { FaPlus, FaCode, FaEye, FaEyeSlash, FaRobot, FaKey, FaSave, FaTrash, FaCopy, FaCheck, FaSpinner, FaFileUpload } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

export default function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [view, setView] = useState('dashboard'); // 'dashboard', 'editor'
  
  // Blog Form State
  const [currentBlog, setCurrentBlog] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [industry, setIndustry] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [status, setStatus] = useState('published'); // draft, published, scheduled
  
  // Editor State
  const [editorMode, setEditorMode] = useState('visual'); // 'visual', 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [aiTopic, setAiTopic] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const visualEditorRef = useRef(null);

  // API Key State
  const [apiKey, setApiKey] = useState('');
  const [apiKeyExpiresAt, setApiKeyExpiresAt] = useState(null);
  const [selectedLang, setSelectedLang] = useState('curl');
  
  // Modal & Visibility State
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyExpirationDays, setKeyExpirationDays] = useState('30'); // Default 30 days
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [isCustomIndustry, setIsCustomIndustry] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);
  
  const fileInputRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem('authData') || '{}');
  const token = userData?.token;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setBlogs(res.data.blogs);
        const uniqueIndustries = [...new Set(res.data.blogs.map(b => b.industry))].filter(Boolean);
        setAvailableIndustries(uniqueIndustries);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  const handleCreateNew = () => {
    setCurrentBlog(null);
    setTitle('');
    setSlug('');
    setIndustry(availableIndustries[0] || '');
    setIsCustomIndustry(availableIndustries.length === 0);
    setThumbnailUrl('');
    setMetaTitle('');
    setMetaDescription('');
    setKeywords('');
    setPublishedAt('');
    setStatus('published');
    setHtmlContent('');
    setView('editor');
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setTitle(blog.title);
    setSlug(blog.slug);
    setIndustry(blog.industry);
    setIsCustomIndustry(!availableIndustries.includes(blog.industry));
    setThumbnailUrl(blog.thumbnailUrl || '');
    setMetaTitle(blog.metaTitle || '');
    setMetaDescription(blog.metaDescription || '');
    setKeywords(blog.keywords?.join(', ') || '');
    setPublishedAt(blog.publishedAt ? new Date(blog.publishedAt).toISOString().slice(0, 16) : '');
    setStatus(blog.status);
    setHtmlContent(blog.htmlContent);
    setView('editor');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlogs();
    } catch (err) {
      alert('Failed to delete blog');
    }
  };

  const handleSave = async (saveStatus) => {
    if (!title.trim() || !htmlContent.trim()) {
      alert("Please provide at least a Blog Title and Content before saving.");
      return;
    }
    
    setIsSaving(saveStatus || status || 'save');
    try {
      const payload = {
        title,
        slug,
        industry: isCustomIndustry ? industry : industry, // industry holds the string either way
        thumbnailUrl,
        metaTitle,
        metaDescription,
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k),
        publishedAt: publishedAt || Date.now(),
        status: saveStatus || status,
        htmlContent
      };

      if (currentBlog) {
        await axios.put(`${API_BASE_URL}/api/blogs/${currentBlog._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchBlogs();
      setView('dashboard');
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to save blog');
    } finally {
      setIsSaving(false);
    }
  };

  const generateWithAI = async () => {
    if (!aiTopic) return alert('Please enter a topic first.');
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/blogs/generate-ai`, { topic: aiTopic }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setTitle(res.data.blog.title);
        setSlug(res.data.blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
        setMetaTitle(res.data.blog.metaTitle);
        setMetaDescription(res.data.blog.metaDescription);
        setKeywords(res.data.blog.keywords?.join(', '));
        setHtmlContent(res.data.blog.htmlContent);
        if (visualEditorRef.current) {
          visualEditorRef.current.innerHTML = res.data.blog.htmlContent;
        }
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to generate content. Please ensure AI Keys are configured.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = evt.target.result;
      setHtmlContent(result);
      if (visualEditorRef.current) {
        visualEditorRef.current.innerHTML = result;
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const confirmGenerateKey = async () => {
    setIsGeneratingKey(true);
    try {
      const payload = { expiresInDays: keyExpirationDays === 'null' ? null : parseInt(keyExpirationDays) };
      const res = await axios.post(`${API_BASE_URL}/api/blogs/generate-key`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setApiKey(res.data.apiKey);
        setApiKeyExpiresAt(res.data.expiresAt);
        setShowKeyModal(false);
        setShowKeySecret(false);
        alert(res.data.message);
      }
    } catch (err) {
      alert('Failed to generate API Key');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(getSnippet());
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  const handleVisualEdit = (e) => {
    setHtmlContent(e.currentTarget.innerHTML);
  };

  useEffect(() => {
    if (editorMode === 'visual' && visualEditorRef.current && visualEditorRef.current.innerHTML !== htmlContent) {
      visualEditorRef.current.innerHTML = htmlContent;
    }
  }, [editorMode, htmlContent]);


  const getSnippet = () => {
    // Attempt to extract the external host, falling back to toxbox api if undefined
    const externalHost = API_BASE_URL || "https://api.toxbox.com";
    const key = apiKey || "YOUR_GENERATED_API_KEY";
    switch (selectedLang) {
      case 'curl':
        return `curl -X GET "${externalHost}/api/blogs/external?page=1&limit=10" \\
  -H "X-toxbox-api-key: ${key}"`;
      case 'node':
        return `const axios = require('axios');
axios.get('${externalHost}/api/blogs/external?slug=my-blog', {
  headers: { 'X-toxbox-api-key': '${key}' }
}).then(res => console.log(res.data));`;
      case 'python':
        return `import requests
headers = {'X-toxbox-api-key': '${key}'}
res = requests.get('${externalHost}/api/blogs/external?page=1', headers=headers)
print(res.json())`;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Blog Management & Integration</h2>
        {view === 'dashboard' ? (
          <button className={styles.primaryBtn} onClick={handleCreateNew}>
            <FaPlus /> Create New Blog
          </button>
        ) : (
          <button className={styles.secondaryBtn} onClick={() => setView('dashboard')}>
            Back to Dashboard
          </button>
        )}
      </div>

      {view === 'dashboard' && (
        <>
          {/* Integration Panel */}
          <div className={styles.integrationPanel}>
            <h3>External API Integration</h3>
            <p>Generate a secure API key to display these blogs on your own website or platform.</p>
            <div className={styles.integrationControls}>
              <button className={styles.actionBtn} onClick={() => setShowKeyModal(true)}>
                <FaKey /> Generate API Key
              </button>
              <select value={selectedLang} onChange={e => setSelectedLang(e.target.value)} className={styles.selectInput}>
                <option value="curl">cURL</option>
                <option value="node">Node.js</option>
                <option value="python">Python</option>
              </select>
            </div>
            
            {apiKey && (
              <div className={styles.apiKeyAlert}>
                <div className={styles.apiKeyHeader}>
                  <strong>Your New API Key</strong>
                  <span className={styles.expirationBadge}>
                    {apiKeyExpiresAt ? `Expires: ${new Date(apiKeyExpiresAt).toLocaleDateString()}` : 'Lifetime (Never Expires)'}
                  </span>
                </div>
                <div className={styles.apiSecretContainer}>
                  <input 
                    type={showKeySecret ? "text" : "password"} 
                    value={apiKey} 
                    readOnly 
                    className={styles.apiSecretInput}
                  />
                  <button className={styles.iconBtn} onClick={() => setShowKeySecret(!showKeySecret)} title="Hide/Unhide">
                    {showKeySecret ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  <button className={styles.iconBtn} onClick={handleCopyKey} title="Copy Key">
                    {copiedKey ? <FaCheck style={{ color: 'green' }} /> : <FaCopy />}
                  </button>
                </div>
                <small className={styles.warningText}>Copy this now! If you refresh the page, you will not be able to see this key again.</small>
              </div>
            )}
            
            <div className={styles.codeSnippet}>
              <pre>{getSnippet()}</pre>
              <button className={styles.copyBtn} onClick={handleCopySnippet}>
                {copiedSnippet ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy Snippet</>}
              </button>
            </div>
          </div>

          {/* Blog Cards */}
          <div className={styles.grid}>
            {blogs.map(blog => (
              <div key={blog._id} className={styles.card}>
                {blog.thumbnailUrl && <img src={blog.thumbnailUrl} alt="Thumbnail" className={styles.cardImage} />}
                <div className={styles.cardBody}>
                  <div className={styles.cardBadges}>
                    <span className={`${styles.badge} ${styles[blog.status]}`}>{blog.status}</span>
                    <span className={styles.badgeIndustry}>{blog.industry}</span>
                  </div>
                  <h3>{blog.title}</h3>
                  <small>Slug: {blog.slug}</small>
                  {blog.status === 'scheduled' && (
                    <div className={styles.scheduledAlert}>
                      Scheduled for: {new Date(blog.publishedAt).toLocaleString()}
                    </div>
                  )}
                  <div className={styles.cardActions}>
                    <button onClick={() => setPreviewBlog(blog)} className={styles.previewBtn} title="Preview Blog">
                      <FaEye/>
                    </button>
                    <button onClick={() => handleEdit(blog)} className={styles.editBtn} title="Edit Blog">Edit</button>
                    <button onClick={() => handleDelete(blog._id)} className={styles.deleteBtn} title="Delete Blog"><FaTrash/></button>
                  </div>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <p>No blogs found. Create one to get started!</p>}
          </div>
        </>
      )}

      {view === 'editor' && (
        <div className={styles.editorLayout}>
          <div className={styles.editorSidebar}>
            <h3>Metadata</h3>
            <div className={styles.formGroup}>
              <label>Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>Slug (URL path)</label>
              <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="Leave blank to auto-generate" />
            </div>
            <div className={styles.formGroup}>
              <label>Industry</label>
              {!isCustomIndustry && availableIndustries.length > 0 ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select 
                    value={industry} 
                    onChange={e => setIndustry(e.target.value)}
                    style={{ flexGrow: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                  >
                    <option value="">Select an Industry...</option>
                    {availableIndustries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => { setIsCustomIndustry(true); setIndustry(''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                    + New
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    value={industry} 
                    onChange={e => setIndustry(e.target.value)} 
                    placeholder="E.g., Healthcare or Finance"
                    style={{ flexGrow: 1 }}
                  />
                  {availableIndustries.length > 0 && (
                    <button type="button" onClick={() => { setIsCustomIndustry(false); setIndustry(availableIndustries[0] || ''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label>Thumbnail URL</label>
              <input type="text" value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} placeholder="https://..." />
              {thumbnailUrl && (
                 <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <img src={thumbnailUrl} alt="Thumbnail Preview" style={{ width: '100%', height: 'auto', display: 'block' }} onError={(e) => e.target.style.display = 'none'} />
                 </div>
              )}
            </div>
            
            <hr/>
            <h3>SEO Tags</h3>
            <div className={styles.formGroup}>
              <label>Meta Title</label>
              <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>Meta Description</label>
              <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>Keywords (comma separated)</label>
              <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} />
            </div>

            <hr/>
            <h3>Publishing</h3>
            <div className={styles.formGroup}>
              <label>Publish Date/Time</label>
              <input type="datetime-local" value={publishedAt} onChange={e => setPublishedAt(e.target.value)} />
            </div>
            
            <div className={styles.saveActions}>
              <button className={styles.secondaryBtn} onClick={() => setView('dashboard')}>Cancel</button>
              <button className={styles.secondaryBtn} onClick={() => handleSave('draft')} disabled={isSaving}>
                {isSaving === 'draft' ? 'Saving...' : 'Save Draft'}
              </button>
              <button className={styles.secondaryBtn} onClick={() => handleSave('scheduled')} disabled={isSaving}>
                {isSaving === 'scheduled' ? 'Scheduling...' : 'Schedule'}
              </button>
              <button className={styles.primaryBtn} onClick={() => handleSave('published')} disabled={isSaving}>
                {isSaving === 'published' ? 'Publishing...' : 'Publish Now'}
              </button>
            </div>
          </div>

          <div className={styles.editorMain}>
            <div className={styles.aiPanel}>
              <input 
                type="text" 
                placeholder="Topic for AI to write about..." 
                value={aiTopic} 
                onChange={e => setAiTopic(e.target.value)} 
                disabled={isGenerating}
              />
              <button 
                type="button" 
                className={styles.aiBtn} 
                onClick={generateWithAI} 
                disabled={isGenerating}
                title="Generates a colorful, styled HTML blog using Sarvam AI"
              >
                {isGenerating ? <FaSpinner className={styles.spinAnimation} /> : <FaRobot />} 
                {isGenerating ? ' Writing...' : ' Auto-Write with AI'}
              </button>
              
              <span className={styles.divider}></span>
              
              <input 
                type="file" 
                accept=".html,.txt" 
                style={{ display: 'none' }} 
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <button 
                type="button" 
                className={styles.importBtn} 
                onClick={() => fileInputRef.current?.click()}
                title="Import HTML or Text document"
              >
                <FaFileUpload /> Import Doc
              </button>
            </div>

            <div className={styles.editorTabs}>
              <button 
                className={editorMode === 'visual' ? styles.activeTab : ''} 
                onClick={() => setEditorMode('visual')}
              >
                <FaEye/> Visual Preview (WYSIWYG)
              </button>
              <button 
                className={editorMode === 'html' ? styles.activeTab : ''} 
                onClick={() => setEditorMode('html')}
              >
                <FaCode/> Raw HTML
              </button>
            </div>

            <div className={styles.editorArea}>
              {editorMode === 'html' ? (
                <textarea
                  className={styles.htmlTextarea}
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="Paste or write your HTML here..."
                />
              ) : (
                <div 
                  className={styles.visualEditor}
                  contentEditable={true}
                  ref={visualEditorRef}
                  onInput={handleVisualEdit}
                  dangerouslySetInnerHTML={{ __html: htmlContent || '<p>Type your blog content here or use AI to generate it...</p>' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showKeyModal && (
        <div className={styles.modalOverlay} onClick={() => setShowKeyModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3>Generate API Key</h3>
            <p>Choose expiration time:</p>
            <select value={keyExpirationDays} onChange={e => setKeyExpirationDays(e.target.value)} className={styles.keySelect}>
              <option value="7">7 Days</option>
              <option value="30">30 Days</option>
              <option value="90">90 Days</option>
              <option value="null">Lifetime (Never Expires)</option>
            </select>
            <div className={styles.modalActions}>
              <button onClick={() => setShowKeyModal(false)} className={styles.secondaryBtn} disabled={isGeneratingKey}>Cancel</button>
              <button onClick={confirmGenerateKey} className={styles.actionBtn} disabled={isGeneratingKey}>
                {isGeneratingKey ? 'Generating...' : 'Generate Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewBlog && (
        <div className={styles.modalOverlay} onClick={() => setPreviewBlog(null)}>
          <div className={styles.previewModal} onClick={e => e.stopPropagation()}>
            <button className={styles.closePreviewBtn} onClick={() => setPreviewBlog(null)}>&times;</button>
            <div className={styles.previewModalHeader}>
                <h2>{previewBlog.title}</h2>
                <span className={styles.badgeIndustry}>{previewBlog.industry || 'General'}</span>
            </div>
            <div className={styles.previewModalContent} dangerouslySetInnerHTML={{ __html: previewBlog.htmlContent }} />
          </div>
        </div>
      )}
    </div>
  );
}
